/**
 * Era scene — the Kaaba of the 2030s (Vision 2030 masterplan). Registered under
 * the era id `kaaba-2030` (matches the backend scene id; i18n labelKey `y2030s`,
 * title "2030s"). Replaces the former `kaaba-2030s` stub that reused a live
 * Kaaba splat variant.
 *
 * Same shape as the other real era scenes (`kaabaOttoman.ts` / `kaaba605.ts`):
 * STANDALONE world-scale config, child of `kaaba` on the stack, entered via the
 * slider's Explore CTA / the menu's «Historical Scenes» tab.
 *
 * Splat transform: -10 on Y, cancelling the offset the v3 export ships with
 * (see `splat.defaultTransform`). In world space the scene therefore spans
 * ~±3 units in XZ with the ground at y ≈ -0.9 and the tallest structures ≈ +0.8.
 *
 * NOT FULLY TUNED — no colliders. Camera constraints so far: 87° polar cap and
 * a 4.6 orbit-distance ceiling; height clamps stay wide open. Camera poses come
 * from `?dev` → Camera; add colliders via `?dev` → Colliders.
 */
import type { SceneConfig } from '../sceneConfig';
import { resolveCdnUrl } from '../../utils/cdnUrl';

const KAABA_FUTURE_2M_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_Future_v3_2M.sog');
const KAABA_FUTURE_1M_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_Future_v3_1M.sog');
const KAABA_FUTURE_600K_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_Future_v3_600K.sog');

const TARGET_POINT = { x: -0.02, y: -0.54, z: -0.01 };

export const kaabaFutureSceneConfig: SceneConfig = {
  id: 'kaaba-2030',
  renderer: {
    clearColor: 0x0f0f13,
  },
  camera: {
    fov: 65,
    near: 0.01,
    far: 1e4,
    initialPosition: { x: -0.08, y: -0.55, z: -0.88 },
    previewTourStart: { x: -0.34, y: 2.26, z: -5.95 },
    previewTourEnd: {
      position: { x: 0.35, y: 1.06, z: 2.89 },
      target: TARGET_POINT,
    },
    flyover: {
      delay: 1.2,
      segments: [
        {
          type: 'waypoint',
          position: { x: -1.615, y: 0.26, z: 0.194 },
          target: TARGET_POINT,
          duration: 4.5,
        },
        {
          type: 'waypoint',
          position: { x: -0.39, y: 1.06, z: -2.91 },
          target: TARGET_POINT,
          duration: 3,
        },
      ],
    },
  },
  poi: {
    clusterEnabled: false,
  },
  // Free orbit apart from the polar cap and the pull-out limit — no height clamps.
  controls: {
    orbitMinDistance: 0,
    // Hold the camera inside the complex — pulled out further, the masterplan
    // reads as a model on a table.
    orbitMaxDistance: 4.6,
    orbitMinPolarAngle: 0,
    // Cap the lowest tilt at polar 87° — camera can't drop below ~horizontal.
    orbitMaxPolarAngle: (87 / 180) * Math.PI,
    constrainVertical: false,
    clampMinHeight: -Infinity,
    clampMaxHeight: Infinity,
    clampMaxHeightFar: Infinity,
    orbitCenter: TARGET_POINT,
  },
  colliders: [],
  splat: {
    defaultByQuality: {
      desktop: 'kaaba_future_2m',
      mobile: {
        high: 'kaaba_future_1m',
        middle: 'kaaba_future_1m',
        low: 'kaaba_future_600k',
      },
    },
    // The v3 re-export sits exactly +10 units up the Y axis (same geometry, same
    // XZ). Shifting it back keeps the camera poses, the reveal origin and the
    // backend POI coordinates — all authored against the origin frame — valid.
    // Drop this once the model is exported at the origin again.
    defaultTransform: { position: { x: 0, y: -10, z: 0 } },
    variants: [
      { key: 'kaaba_future_2m', url: KAABA_FUTURE_2M_URL },
      { key: 'kaaba_future_1m', url: KAABA_FUTURE_1M_URL },
      { key: 'kaaba_future_600k', url: KAABA_FUTURE_600K_URL },
    ],
    revealAnimationDuration: 2.5,
    // Scene radius is ~3 units — roughly half the spread of the Ottoman scene.
    revealDistanceScale: 0.6,
    revealMode: '3d',
    revealOrigin: TARGET_POINT,
  },
  previewModel: {
    url: resolveCdnUrl('preview/Kaaba_Future_preloader_pnts_150000_meshopt(16).glb'),
    // Same +10 Y offset as the splat — the preloader cloud must morph into it.
    transform: { position: { x: 0, y: -10, z: 0 } },
    style: {
      // Points are drawn at a constant pixel size, so density on screen scales
      // with the entry-pose distance. This pose sits 3.33 from the target where
      // the first one sat 4.39 — 1.73× the projected area — and 2.5 · √1.73
      // brings points-per-pixel back to what the cloud read like before.
      pointSizeFactor: 3.3,
      // The cloud's own XZ centroid, deliberately NOT `TARGET_POINT`: the wave
      // has to sweep out of the middle of the city, and the orbit center moves
      // with camera tuning.
      wavesCenter: { x: 0.05, y: -0.55, z: 0.75 },
    },
  },
  assistant: {
    position: { x: 0, y: 0, z: -0.03 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    hemisphereLight: {
      skyColor: 0xffe6c4,
      groundColor: 0x1a2027,
      intensity: 0.45,
    },
    directionalLights: [
      {
        color: 0xfff2d5,
        intensity: 0.85,
        position: { x: -0.8, y: 1.2, z: 0.6 },
      },
      {
        color: 0xbdd4ff,
        intensity: 0.35,
        position: { x: 0.9, y: 0.6, z: -0.45 },
      },
    ],
    background: {
      color: 0x0f0f13,
      skybox: {
        url: resolveCdnUrl('environment/autumn_field_puresky_8k.jpg'),
        mobileUrl: resolveCdnUrl('environment/autumn_field_puresky_2k.jpg'),
        intensity: 1.0,
        blur: 0.0,
        rotation: [0, 205, 0],
      },
    },
  },
};
